import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { Body, Box, Link } from '@hover/blueprint';
import type { IMeasurePros } from '../interfaces/IHoverPros';
import IndAdjCheck from '../assets/svgs/IndAdjCheck';
import SandClockVector from '../assets/svgs/SandClockVector';
import SpeedMeter from '../assets/svgs/SpeedMeter';
import { ISolutionPoints } from '../interfaces/ISolutionPoints';
import { SolutionTestimonyItem } from '../interfaces/ISolutionTestimony';
import { IFaqQuestion } from '../interfaces/IFaqQuestion';
import type { ILearningCardProps } from '../components/common/learning-card';
import { ISolutionPartnerTableProps } from '../interfaces/ISolutionsPartners';

export const INDEPENDENT_ADJUSTERS_PROS: IMeasurePros = [
  {
    description: (
      <>
        Complete the
        <br />
        job faster
      </>
    ),
    icon: <SpeedMeter />,
    body: 'Eliminate time-intensive hand measurements and sketches',
    key: 1,
    isVector: true,
  },
  {
    description: (
      <>
        Submit claims with
        <br />
        confidence
      </>
    ),
    icon: <IndAdjCheck />,
    body: 'Minimize errors, site revisits, questions from the desk',
    key: 2,
    isVector: true,
  },
  {
    description: (
      <>
        Spend less time
        <br />
        at the edges
      </>
    ),
    icon: <SandClockVector />,
    body: 'Reduce the need to climb roofs and other hazards',
    key: 3,
    isVector: true,
  },
];

export const SUBMIT_CLAIMS_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content:
      'Minimize errors, site revisits, questions from the desk, and supplements down the road.',
  },
  {
    id: 2,
    content:
      'Pass your property measurements, damage photos, and plans directly into Xactimate, ClaimsConnect, and SettleAssist.',
  },
];

export const COMPLETE_JOBS_FASTER_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content: 'Complete property measurements in a fraction of the time',
  },
  {
    id: 2,
    content: 'Reduce time-consuming hand-measurements and plans',
  },
  {
    id: 3,
    content:
      'Get detailed measurements even when part of the structure is missing',
  },
];

export const SINGLE_SOLUTION_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content:
      'Capture detailed measurements, plans, damage photos, and a 3D model for any interior or exterior claim.',
  },
  {
    id: 2,
    content: 'Fast onboarding with no special training',
  },
  {
    id: 3,
    content: 'Reduce the time it takes to build and submit a claims package',
  },
];

export const INDEPENDENT_ADJUSTERS_TESTIMONIES: SolutionTestimonyItem[] = [
  {
    quote:
      "Our adjusters can see more properties each day because they're not hand measuring, which reduces cycle times, increases our claims handling capacity [by 10% - 15% per person], and broadens our service areas.",
    name: 'JOEY LANG',
    role: 'CAT Claims unlimited - florida',
    image: (
      <StaticImage
        objectFit="contain"
        src="../assets/images/measure_testimony_3.png"
        alt=""
      />
    ),
    logo: (
      <StaticImage
        src="../assets/images/measure_testimony-logo_3.jpg"
        alt="J&B"
      />
    ),
    id: 1,
  },
];

export const INDEPENDENT_ADJUSTERS_FAQ: Array<IFaqQuestion> = [
  {
    id: 0,
    title: 'Why should I consider HOVER for my property inspections?',
    content: (
      <Body mb={500}>
        HOVER delivers interior and exterior property measurements in a fraction
        of the time it takes with traditional methods. And it will even work if
        part of a structure is missing or damaged.
      </Body>
    ),
  },
  {
    id: 1,
    title:
      'Can I have the policyholder perform the inspection before I arrive?',
    content: (
      <Body mb={500}>
        Yes. HOVER is easy enough for anyone to use without special training or
        equipment. Simply send an invite to the homeowner. HOVER will perform
        the reconstruction and inform you once the measurements, plans, and
        model are available.
      </Body>
    ),
  },
  {
    id: 2,
    title: 'Does HOVER integrate with my other solutions?',
    content: (
      <Body mb={500}>
        HOVER integrates with a number of software solutions including, Verisk
        Xactimate, Core Logic Claims Connect, and others.{' '}
        <Link href="/partners/#integrations" fontWeight={500}>
          Explore Integrations.
        </Link>
      </Body>
    ),
  },
];

export const INDEPENDENT_ADJUSTERS_RESOURCES: ILearningCardProps['card'][] = [
  {
    id: 1,
    description:
      'HOVER for claims - Learn how to successfully take photos of any property with the HOVER app.',
    title: 'VIDEO',
    linkTitle: 'Watch the video',
    linkTo:
      'https://fast.wistia.com/embed/channel/5ds5e3st4r?wchannelid=5ds5e3st4r&wmediaid=ntetqcsiqb',
    titleColor: 'neutral.600',
    image: (
      <Box borderRadius={8} overflow="hidden">
        <StaticImage
          placeholder="blurred"
          loading="lazy"
          alt=""
          src="../assets/images/independent_adjusters_resources_1.png"
        />
      </Box>
    ),
    mobileImage: (
      <Box borderRadius={8} overflow="hidden" w="100%">
        <StaticImage
          placeholder="blurred"
          loading="lazy"
          alt=""
          src="../assets/images/independent_adjusters_resources_1.png"
          style={{ width: '100%' }}
        />
      </Box>
    ),
  },
  {
    id: 2,
    description:
      'Dive into what the virtual claims process is and how you can streamline it using HOVER',
    title: 'BLOG',
    linkTitle: 'Read the blog',
    titleColor: 'neutral.600',
    linkTo:
      'https://hover.to/blog/streamline-the-virtual-claims-insurance-process/',
    image: (
      <Box borderRadius={8} overflow="hidden">
        <StaticImage
          placeholder="blurred"
          loading="lazy"
          alt=""
          src="../assets/images/independent_adjusters_resources_2.png"
        />
      </Box>
    ),
    mobileImage: (
      <Box borderRadius={8} overflow="hidden" w="100%">
        <StaticImage
          placeholder="blurred"
          loading="lazy"
          alt=""
          src="../assets/images/independent_adjusters_resources_2.png"
          style={{ width: '100%' }}
        />
      </Box>
    ),
  },
  {
    id: 3,
    description:
      'Bees360 saves 40-70% of claim adjustment expenses and increases claim capacity.',
    title: 'CASE STUDY',
    linkTitle: 'Download case study',
    titleColor: 'neutral.600',
    linkTo: 'https://view.highspot.com/viewer/63fff414f8537d649b491e14',
    image: (
      <Box borderRadius={8} overflow="hidden">
        <StaticImage
          placeholder="blurred"
          loading="lazy"
          alt=""
          src="../assets/images/independent_adjusters_resources_3.png"
        />
      </Box>
    ),
    mobileImage: (
      <Box borderRadius={8} overflow="hidden" w="100%">
        <StaticImage
          placeholder="blurred"
          loading="lazy"
          alt=""
          src="../assets/images/independent_adjusters_resources_3.png"
          style={{ width: '100%' }}
        />
      </Box>
    ),
  },
];

export const INDEPENDENT_ADJUSTERS_PARTNERS: ISolutionPartnerTableProps['partners'] =
  [
    {
      key: 1,
      logo: (
        <StaticImage
          src="../assets/images/travelers_vector_logo.png"
          alt="StateFarm"
        />
      ),
    },
    {
      key: 2,
      logo: (
        <StaticImage
          src="../assets/images/independent_adjusters_partner_2.png"
          alt="Travellers"
        />
      ),
    },
    {
      key: 3,
      logo: (
        <StaticImage
          src="../assets/images/independent_adjusters_partner_3.png"
          alt="DaBella"
        />
      ),
    },
    {
      key: 4,
      logo: (
        <StaticImage
          src="../assets/images/independent_adjusters_partner_4.png"
          alt="Renewal by Andersen"
        />
      ),
    },
    {
      key: 5,
      logo: (
        <StaticImage
          src="../assets/images/independent_adjusters_partner_5.png"
          alt="ErieHome"
        />
      ),
    },
    {
      key: 6,
      logo: (
        <StaticImage
          src="../assets/images/independent_adjusters_partner_6.png"
          alt="ErieHome"
        />
      ),
    },
    {
      key: 7,
      logo: (
        <StaticImage
          src="../assets/images/independent_adjusters_partner_7.png"
          alt="ErieHome"
        />
      ),
    },
    {
      key: 8,
      logo: (
        <StaticImage
          src="../assets/images/independent_adjusters_partner_8.png"
          alt="ErieHome"
        />
      ),
    },
  ];
